import { useMemo, useState } from "react";
import { ArrowUpDown, Settings as SettingsIcon, SlidersHorizontal, Users, X } from "lucide-react";
import { T } from "../lib/theme";
import { AVG_MONTH_DAYS, daysSince } from "../lib/dates";
import type { MemberSummary, Meeting } from "../lib/types";
import type { Settings } from "../lib/settings";
import { SORTS, activeFilterCount, useFilters, type SortKey } from "../lib/filters";
import { historyFor } from "../hooks/useData";
import { EmptyState, Header, SearchField, VirtualList } from "../components/ui";
import { FiltersSheet } from "../components/FiltersSheet";
import { PersonRow, ROW_H } from "../components/PersonRow";
import { HistorySheet } from "../components/HistorySheet";

export function UpNext({
  members,
  meetings,
  settings,
  onToggleStar,
  onManage,
  onSettings,
}: {
  members: MemberSummary[];
  meetings: Meeting[];
  settings: Settings;
  onToggleStar: (m: MemberSummary) => void;
  onManage: () => void;
  onSettings: () => void;
}) {
  const { filters, set, reset } = useFilters();
  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [trayOpen, setTrayOpen] = useState(true);

  const activeCount = activeFilterCount(filters);
  const activeMembers = useMemo(() => members.filter((m) => m.status === "active"), [members]);

  const rows = useMemo(() => {
    const alpha = (a: MemberSummary, b: MemberSummary) =>
      a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name);

    // "Never spoken" is the far end of the scale, not a missing value, so it
    // stays pinned to the due end whichever way the date sort points.
    const byDate = (a: MemberSummary, b: MemberSummary, dir: number) => {
      if (!a.last_spoke_date && !b.last_spoke_date) return alpha(a, b);
      if (!a.last_spoke_date) return -dir;
      if (!b.last_spoke_date) return dir;
      const c = a.last_spoke_date.localeCompare(b.last_spoke_date) * dir;
      return c !== 0 ? c : alpha(a, b);
    };

    const cmp: Record<SortKey, (a: MemberSummary, b: MemberSummary) => number> = {
      due: (a, b) => byDate(a, b, 1),
      recent: (a, b) => byDate(a, b, -1),
      az: alpha,
      za: (a, b) => alpha(b, a),
    };

    const needle = q.trim().toLowerCase();
    return activeMembers
      .filter((m) => (m.speaker_type === "youth" ? filters.youth : filters.adults))
      .filter((m) =>
        filters.spoken === "all" ? true
        : filters.spoken === "only-never" ? !m.last_spoke_date
        : Boolean(m.last_spoke_date),
      )
      .filter(
        (m) =>
          !filters.hideRecent ||
          !m.last_spoke_date ||
          daysSince(m.last_spoke_date) > settings.recentMonths * AVG_MONTH_DAYS,
      )
      .filter(
        (m) =>
          !needle ||
          `${m.last_name} ${m.first_name}`.toLowerCase().includes(needle) ||
          `${m.first_name} ${m.last_name}`.toLowerCase().includes(needle),
      )
      .sort(cmp[filters.sort]);
  }, [activeMembers, q, filters, settings.recentMonths]);

  const starred = useMemo(
    () =>
      members
        .filter((m) => m.is_considering && m.status === "active")
        .sort((a, b) => a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name)),
    [members],
  );

  const openMember = open ? members.find((m) => m.id === open) : undefined;

  return (
    <>
      <Header
        title="Up next to speak"
        sub={`${rows.length} shown`}
        subDim
        right={
          <div style={{ display: "flex", gap: 2, marginTop: 2 }}>
            <button onClick={onManage} aria-label="Ward members" style={{ color: "rgba(255,255,255,.85)", padding: 7 }}>
              <Users size={20} />
            </button>
            <button onClick={onSettings} aria-label="Settings" style={{ color: "rgba(255,255,255,.85)", padding: 7 }}>
              <SettingsIcon size={20} />
            </button>
          </div>
        }
      />

      <div style={{ background: T.navy, padding: "0 18px 16px" }}>
        <div style={{ marginBottom: 10 }}>
          <SearchField value={q} onChange={setQ} placeholder="Search by last name" />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={() => setFiltersOpen(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "8px 13px",
              borderRadius: 9,
              fontSize: 13,
              fontWeight: 600,
              // Brass when something is hiding people, so a filter left on last
              // week is visible rather than silently dropping someone today.
              background: activeCount ? T.brass : "rgba(255,255,255,.12)",
              color: "#fff",
            }}
          >
            <SlidersHorizontal size={14} />
            Filters &amp; sort
            {activeCount > 0 && (
              <span
                style={{
                  background: "rgba(255,255,255,.25)",
                  borderRadius: 999,
                  padding: "1px 7px",
                  fontSize: 11.5,
                }}
              >
                {activeCount}
              </span>
            )}
          </button>

          {/* Same icon as the sheet's Sort by heading, so it's clear this
              label is reporting the sort rather than being its own control. */}
          <span
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              color: "rgba(255,255,255,.55)",
            }}
          >
            <ArrowUpDown size={13} />
            {SORTS.find((x) => x.v === filters.sort)!.short}
          </span>
        </div>
      </div>

      {starred.length > 0 && (
        <div style={{ background: T.brassSoft, borderBottom: `1px solid ${T.brassLine}` }}>
          <button
            onClick={() => setTrayOpen((x) => !x)}
            style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 18px" }}
          >
            <span className="eyebrow" style={{ color: T.brass }}>
              Considering · {starred.length}
            </span>
            <span style={{ fontSize: 12, color: T.brass, fontWeight: 600 }}>{trayOpen ? "Hide" : "Show"}</span>
          </button>
          {trayOpen && (
            <div style={{ padding: "0 18px 12px", display: "flex", flexWrap: "wrap", gap: 7 }}>
              {starred.map((m) => (
                <div
                  key={m.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    background: "#fff",
                    border: `1px solid ${T.brassLine}`,
                    borderRadius: 999,
                    padding: "5px 6px 5px 12px",
                  }}
                >
                  <span className="serif" style={{ fontSize: 14.5 }}>
                    {m.last_name}, {m.first_name}
                  </span>
                  <button
                    onClick={() => onToggleStar(m)}
                    aria-label={`Remove ${m.last_name}, ${m.first_name}`}
                    style={{ color: T.faint, display: "flex", padding: 2 }}
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ background: "#fff" }}>
        {rows.length === 0 && (
          <EmptyState>
            {!filters.adults && !filters.youth
              ? "Adults and Youth are both switched off."
              : q.trim()
                ? `Nobody matches “${q.trim()}”.`
                : "No one matches those filters."}
            {activeCount > 0 && (
              <>
                {" "}
                <button
                  onClick={reset}
                  style={{ color: T.accent, fontWeight: 600, textDecoration: "underline" }}
                >
                  Clear filters
                </button>
              </>
            )}
          </EmptyState>
        )}
        <VirtualList
          items={rows}
          rowHeight={ROW_H}
          renderRow={(m) => (
            <PersonRow
              key={m.id}
              m={m}
              targetMonths={settings.targetMonths}
              starred={m.is_considering}
              onOpen={() => setOpen(m.id)}
              onStar={() => onToggleStar(m)}
            />
          )}
        />
      </div>

      {filtersOpen && (
        <FiltersSheet
          filters={filters}
          set={set}
          reset={reset}
          recentMonths={settings.recentMonths}
          shown={rows.length}
          total={activeMembers.length}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      {openMember && (
        <HistorySheet
          m={openMember}
          history={historyFor(openMember.id, meetings)}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}
